import React, { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { useNavigate, useParams } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import useToast from "../../components/common/useToast";
import apiFunctions from "../../services/api-service";
import { AxiosError } from "axios";
import { errorHandler } from "../../utils/HelperFunctions";
import "../../assets/css/datatable.css";
import DeleteConfirmation from "../../components/modal-popup/delete-popup";
import { Paginator, PaginatorCurrentPageReportOptions, PaginatorRowsPerPageDropdownOptions } from "primereact/paginator";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { InputText as PrimeInputText } from "primereact/inputtext";
import { MegaphoneIcon } from "@heroicons/react/24/outline";
import { Calendar } from "primereact/calendar";
import { Skeleton } from "primereact/skeleton";
import WhatsAppTemplateSection from "../../components/common/WhatsAppTemplateSection";
import WhatsAppIcon from "../../components/common/WhatsAppIcon";
import ConfirmActionPopup from "../../components/modal-popup/confirm-action-popup";

const AdvertisementPage: React.FC = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const { screen } = useParams();
    const navigate = useNavigate();
    const [newLeadsData, setNewLeadsData] = useState<any[]>([]);
    const [dataLoad, setDataLoad] = useState(true);
    const [load, setLoad] = useState(true);
    const initialized = useRef(false);
    const [itemsPerPage, setItemsPerPage] = useState<any>(10);
    const [currentPage, setCurrentPage] = useState(0);
    const [skipData, setSkipData] = useState(0);
    const [totalRecords, settotalRecords] = useState(0);
    const [totalPages, settotalPages] = useState<any>(0);
    const { showToast }: any = useToast();
    const [companyToDelete, setCompanyToDelete] = useState<any | null>(null)
    const [sortField, setSortField] = useState<string>("createddate");
    const [sortOrder, setSortOrder] = useState<any>(-1);
    const [deleteConfirmationVisible, setDeleteConfirmationVisible] = useState(false);
    const [name, setName] = useState<string>("");
    const [dateRangeFilter, setDateRangeFilter] = useState<(Date | null)[] | null>(null);

    const [showMessageDialog, setShowMessageDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState<any[]>([]);

    const [waTemplate, setWaTemplate] = useState("");
    const [waParams, setWaParams] = useState<string[]>([]);
    const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
    const [waConfirmVisible, setWaConfirmVisible] = useState(false);
    const [waPendingPayload, setWaPendingPayload] = useState<any>(null);

    const [activeTab, setActiveTab] = useState<"new" | "running" | "expiry">("new");

    const [rerunAd, setRerunAd] = useState<any>(null);
    const [rerunDialog, setRerunDialog] = useState(false);
    const [rerunStartDate, setRerunStartDate] = useState<Date | null>(null);
    const [rerunEndDate, setRerunEndDate] = useState<Date | null>(null);
    const [rerunLoading, setRerunLoading] = useState(false);

    const startIndex = (currentPage - 1) * itemsPerPage;

    const pageTitle = `Advertisements`;

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const openRerunDialog = (rowData: any) => {
        setRerunAd(rowData);
        setRerunStartDate(null);
        setRerunEndDate(null);
        setRerunDialog(true);
    };

    const handleRerunSubmit = async () => {
        if (!rerunStartDate || !rerunEndDate) {
            showToast({ severity: "warn", summary: "Warning", detail: "Please select start and end date", life: 3000 });
            return;
        }

        try {
            setRerunLoading(true);

            const fmt = (d: Date) => {
                const dd = String(d.getDate()).padStart(2, "0");
                const mm = String(d.getMonth() + 1).padStart(2, "0");
                return `${d.getFullYear()}-${mm}-${dd}`;
            };

            const response = await apiFunctions.put(`/advertisement/rerun/${rerunAd._id}`, {
                start_date: fmt(rerunStartDate),
                end_date: fmt(rerunEndDate),
            });

            if (response?.data?.success) {
                showToast({ severity: "success", summary: "Success", detail: "Advertisement rerun successfully", life: 3000 });
                setRerunDialog(false);
                setRerunAd(null);
                getNewLeadsData();
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setRerunLoading(false);
        }
    };

    const actionBodyTemplate = (rowData: any, mobile: boolean = false) => {
        return (
            <div className={`flex items-center gap-2 ${mobile ? "flex-wrap" : "flex-nowrap"}`}>
                <Button label="View" outlined size="small" style={{ color: "#264164", borderColor: "#264164" }} onClick={() => navigate(`/admin/advertisement/view/${rowData._id}`)} />
                <Button label="Edit" outlined size="small" style={{ color: "#1a7a4a", borderColor: "#1a7a4a" }} onClick={() => navigate(`/admin/advertisement/edit/${rowData._id}`)} />
                {!rowData.is_active && (
                    <Button label="Rerun" outlined size="small" style={{ color: "#7c3aed", borderColor: "#7c3aed" }} onClick={() => openRerunDialog(rowData)} />
                )}
                <Button label="Delete" outlined size="small" style={{ color: "#dc2626", borderColor: "#dc2626" }} onClick={() => handleDelete(rowData)} />
            </div>
        );
    };

    const openBulkMessageDialog = () => {
        setSelectedUser(null); // bulk mode
        setTitle("Sagar Samaj");
        setMessage("");
        setShowMessageDialog(true);
    };

    const sendMessage = async () => {

        if (!title.trim()) {
            showToast({
                severity: "warn",
                summary: "Warning",
                detail: "Please enter a title",
                life: 3000
            });
            return;
        }

        if (!message.trim()) {
            showToast({
                severity: "warn",
                summary: "Warning",
                detail: "Please enter a message",
                life: 3000
            });
            return;
        }

        // Collect userIds
        let userIds: string[] = [];

        // Single user
        if (selectedUser?.userid) {
            userIds = [selectedUser.userid];
        }

        // Multiple users
        if (selectedUsers.length > 0) {
            userIds = selectedUsers.map(u => u.userid);
        }

        if (!userIds.length) {
            showToast({
                severity: "error",
                summary: "Error",
                detail: "No users selected",
                life: 3000
            });
            return;
        }

        try {
            setSending(true);

            const payload = {
                userIds,
                title,
                message,
                whatsapp_template: waTemplate,
                whatsapp_params: waParams
            };

            const response = await apiFunctions.post(
                "/activeusers/notifications",
                payload
            );

            if (response.data.success) {

                if (response.data.summary) {

                    const { sent, failed, total } = response.data.summary;

                    showToast({
                        severity: failed > 0 ? "warn" : "success",
                        summary: "Notification Report",
                        detail: `Total: ${total}, Sent: ${sent}, Failed: ${failed}`,
                        life: 5000
                    });

                }
                // If backend is background mode
                else {

                    showToast({
                        severity: "info",
                        summary: "Notification Started",
                        detail: "Notification job is running in background",
                        life: 4000
                    });

                }

                setShowMessageDialog(false);
                setTitle("");
                setMessage("");
                setSelectedUser(null);
                setSelectedUsers([]); // clear selection
                setWaTemplate("");
                setWaParams([]);

            } else {
                throw new Error(response.data.message);
            }

        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setSending(false);
        }
    };

    // Step 1: validate + collect recipients, then confirm before sending
    const sendWhatsAppOnly = async () => {

        if (!waTemplate) {
            showToast({
                severity: "warn",
                summary: "Warning",
                detail: "Please select a WhatsApp template",
                life: 3000
            });
            return;
        }

        let userIds: string[] = [];

        if (selectedUser?.userid) {
            userIds = [selectedUser.userid];
        }

        if (selectedUsers.length > 0) {
            userIds = selectedUsers.map(u => u.userid);
        }

        if (!userIds.length) {
            showToast({
                severity: "error",
                summary: "Error",
                detail: "No users selected",
                life: 3000
            });
            return;
        }

        setWaPendingPayload({
            userIds,
            template: waTemplate,
            params: waParams
        });
        setWaConfirmVisible(true);
    };

    // Step 2: fires only after the admin confirms the recipient count
    const confirmSendWhatsApp = async () => {
        if (!waPendingPayload) return;

        try {
            setSendingWhatsApp(true);

            const response = await apiFunctions.post(
                "/activeusers/whatsapp-notifications",
                waPendingPayload
            );

            if (response.data.success) {

                if (response.data.summary) {
                    const { total, sent, failed } = response.data.summary;
                    showToast({
                        severity: failed > 0 ? "warn" : "success",
                        summary: "WhatsApp Report",
                        detail: `Total: ${total}, Sent: ${sent}, Failed: ${failed}`,
                        life: 5000
                    });
                } else {
                    showToast({
                        severity: "info",
                        summary: "WhatsApp Started",
                        detail: "WhatsApp sending is running in background",
                        life: 4000
                    });
                }

                setShowMessageDialog(false);
                setWaTemplate("");
                setWaParams([]);
                setWaConfirmVisible(false);
                setWaPendingPayload(null);
            }

        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setSendingWhatsApp(false);
        }
    };

    async function getNewLeadsData() {
        try {
            setDataLoad(true);

            const page = currentPage + 1;

            const params: any = {
                page,
                limit: itemsPerPage,
                sortField,
                sortOrder: sortOrder === 1 ? "asc" : "desc",
            };

            if (screen) params.screen = screen;
            params.tab = activeTab;
            if (name) params.search = name;

            const fmt = (d: Date) => {
                const dd = String(d.getDate()).padStart(2, "0");
                const mm = String(d.getMonth() + 1).padStart(2, "0");
                return `${d.getFullYear()}-${mm}-${dd}`;
            };
            const [rangeStart, rangeEnd] = dateRangeFilter || [];
            if (rangeStart) params.start_date = fmt(rangeStart);
            if (rangeEnd) params.end_date = fmt(rangeEnd);

            const response = await apiFunctions.get("/advertisement", { params });

            if (response.data.success) {
                setNewLeadsData(response.data.data);
                settotalRecords(response.data.totalRecords);
                settotalPages(response.data.totalPages);
            } else {
                showToast({
                    severity: "error",
                    summary: "Error",
                    detail: response.data.message,
                    life: 3000,
                });
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setDataLoad(false);
            setLoad(false);
        }
    }

    const confirmDelete = async () => {
        if (companyToDelete) {
            try {
                const response = await apiFunctions.delete(`/advertisement/${companyToDelete._id}`);
                if (response?.data?.success) {
                    showToast({ severity: "success", summary: "Deleted", detail: "Advertisement deleted successfully", life: 3000 });
                } else {
                    showToast({ severity: "error", summary: "Error", detail: response?.data?.message || "Delete failed", life: 3000 });
                }
            } catch (err) {
                errorHandler(err as AxiosError);
            } finally {
                setDeleteConfirmationVisible(false);
                setCompanyToDelete(null);
                getNewLeadsData();
            }
        }
    };

    const handleDelete = (rowData: any) => {
        setCompanyToDelete(rowData);
        setDeleteConfirmationVisible(true);
    };

    const hideDeleteConfirmation = () => {
        setDeleteConfirmationVisible(false);
        setCompanyToDelete(null);
    };

    const onSort = (e: any) => {
        setSortOrder(e.sortOrder);
        setSortField(e.sortField);
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const handleItemsPerPageChange = (value: number) => {
        setItemsPerPage(value);
        setCurrentPage(0);
    };

    const getsearchdata = (e: any) => {
        if (e.target.value.length >= 2) {
            setName(e.target.value);
            setCurrentPage(0);
        } else {
            setName("");
        }
    };

    const handleDateRangeFilterChange = (value: (Date | null)[] | null) => {
        setDateRangeFilter(value);
        setCurrentPage(0);
    };

    useEffect(() => {
        setSkipData(currentPage * itemsPerPage);
    }, [currentPage, itemsPerPage]);

    useEffect(() => {
        if (!load) {
            getNewLeadsData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skipData, itemsPerPage, sortField, sortOrder, activeTab, name, dateRangeFilter]);

    const handleTabChange = (tab: "new" | "running" | "expiry") => {
        setActiveTab(tab);
        setCurrentPage(0);
        setLoad(false);
    };

    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            getNewLeadsData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const header = (
        <div className="grid grid-cols-12 gap-4 items-center table-header">

            {/* Left: Title + Report */}
            <div className="col-span-12 sm:col-span-7 flex flex-col gap-1">
                {/* Page Title */}
                <h2 className="card-title m-0 text-lg sm:text-xl">
                    {pageTitle}
                </h2>
            </div>

            {/* Right: Actions */}
            <div className="col-span-12 sm:col-span-5 flex justify-end gap-2 flex-wrap">

                <div className="search-box flex items-center gap-2 w-full sm:w-auto">
                    <span className="text-gray-600">Search:</span>
                    <PrimeInputText
                        type="text"
                        className="search-input w-full sm:w-auto"
                        placeholder="Search by company, mobile or city..."
                        onChange={(e) => getsearchdata(e)}
                    />
                </div>

                <Calendar
                    value={dateRangeFilter as any}
                    onChange={(e) => handleDateRangeFilterChange(e.value as (Date | null)[] | null)}
                    selectionMode="range"
                    numberOfMonths={2}
                    readOnlyInput
                    showIcon
                    dateFormat="dd/mm/yy"
                    placeholder="Start Date Range"
                    className="w-full sm:w-auto"
                    panelClassName="ad-date-range-picker"
                    showButtonBar
                />

                {selectedUsers.length > 0 && (
                    <Button
                        label={`Send (${selectedUsers.length})`}
                        icon="pi pi-bell"
                        severity="success"
                        onClick={openBulkMessageDialog}
                    />
                )}

                <Button
                    label="Add Advertisement"
                    icon="pi pi-plus"
                    severity="success"
                    onClick={() => navigate("/admin/advertisement/add")}
                />

            </div>

        </div>
    );

    const paginatoreTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport",
        RowsPerPageDropdown: (options: PaginatorRowsPerPageDropdownOptions) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 25, value: 25 },
                { label: 50, value: 50 },
                { label: 100, value: 100 },
                { label: 200, value: 200 },
            ];

            return (
                <div className="paginator-dropdown">
                    <span className="mx-1">Show</span>
                    <Dropdown
                        value={itemsPerPage}
                        options={dropdownOptions}
                        onChange={(e) => handleItemsPerPageChange(e.value)}
                    />
                </div>
            );
        },
        CurrentPageReport: (options: PaginatorCurrentPageReportOptions) => {
            return <span className="entries-text mx-1">entries</span>;
        },
    };

    const tabs: { key: "new" | "running" | "expiry"; label: string }[] = [
        { key: "new", label: "New" },
        { key: "running", label: "Running" },
        { key: "expiry", label: "Expiry" },
    ];

    return (
        <>
            <div className="dashboard-margin">
                {/* Tab Bar */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0", borderBottom: "2px solid #e5e7eb", marginBottom: "0", background: "#fff", paddingLeft: "16px", paddingTop: "12px" }}>
                    {tabs.map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => handleTabChange(tab.key)}
                            style={{
                                padding: "8px 16px",
                                border: "none",
                                borderBottom: activeTab === tab.key ? "2px solid #264164" : "2px solid transparent",
                                background: "none",
                                cursor: "pointer",
                                fontWeight: activeTab === tab.key ? 700 : 400,
                                color: activeTab === tab.key ? "#264164" : "#6b7280",
                                fontSize: "14px",
                                marginBottom: "-2px",
                                whiteSpace: "nowrap",
                                transition: "all 0.15s",
                            }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {isMobile ? (
                    <div className="bg-white w-full overflow-x-hidden box-border" style={{ borderRadius: "8px" }}>
                        <div className="p-3 pb-2">{header}</div>
                        <div className="flex flex-col gap-2 px-3 pb-3">
                            {dataLoad ? (
                                Array.from({ length: itemsPerPage }, (_, i) => (
                                    <div key={i} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                        <Skeleton width="60%" height="1rem" className="mb-2" />
                                        <Skeleton width="40%" height="0.8rem" />
                                    </div>
                                ))
                            ) : newLeadsData.length === 0 ? (
                                <div style={{ textAlign: "center", padding: "64px 0", color: "#9ca3af" }}>
                                    <MegaphoneIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                    <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No advertisements found</p>
                                </div>
                            ) : (
                                newLeadsData.map((rowData: any) => (
                                    <div key={rowData._id} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                        <div className="flex items-start justify-between gap-2 w-full">
                                            <p className="m-0 font-semibold text-sm break-words flex-1 min-w-0" style={{ color: "#264164" }}>{rowData.company_name}</p>
                                            <span className={`px-2 py-1 rounded text-white text-xs flex-shrink-0 ${rowData.is_active ? "bg-green-500" : "bg-red-500"}`}>
                                                {rowData.is_active ? "Active" : "Inactive"}
                                            </span>
                                        </div>
                                        <div className="flex flex-col gap-1 mt-2 text-xs" style={{ color: "#4b5563" }}>
                                            <div><span className="font-medium">Contact:</span> {rowData.whatsapp_number || rowData.mobile_number || "-"}</div>
                                            <div><span className="font-medium">Ad Type:</span> {rowData.ad_type}</div>
                                            <div><span className="font-medium">District:</span> {rowData.city}</div>
                                            <div>
                                                <span className="font-medium">Start:</span> {rowData.start_date ? new Date(rowData.start_date).toLocaleDateString("en-GB") : "-"}
                                                {" · "}
                                                <span className="font-medium">End:</span> {rowData.end_date ? new Date(rowData.end_date).toLocaleDateString("en-GB") : "-"}
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap justify-end gap-1 mt-2">
                                            {actionBodyTemplate(rowData, true)}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                ) : (
                    <DataTable
                        scrollable
                        scrollHeight="calc(100vh - 345px)"
                        style={{ minHeight: "calc(100vh - 320px)", background: "#fff" }}
                        header={header}
                        value={
                            dataLoad
                                ? Array.from({ length: itemsPerPage }, (_, i) => ({ _id: `_skeleton_${i}` }))
                                : newLeadsData
                        }
                        emptyMessage={
                            <div style={{ textAlign: "center", padding: "64px 0", color: "#9ca3af" }}>
                                <MegaphoneIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No advertisements found</p>
                            </div>
                        }
                        sortField={sortField}
                        sortOrder={sortOrder}
                        onSort={onSort}
                        resizableColumns
                        removableSort
                        rows={itemsPerPage}
                    >
                        <Column field="company_name" header="Company Name" sortable style={{ minWidth: "200px" }} body={(rowData) => dataLoad ? <Skeleton /> : rowData.company_name} />
                        <Column
                            header="Contact Number"
                            style={{ minWidth: "160px" }}
                            body={(rowData) => dataLoad ? <Skeleton /> : (rowData.whatsapp_number || rowData.mobile_number || "-")}
                        />
                        <Column field="ad_type" header="Ad Type" sortable style={{ minWidth: "110px" }} body={(rowData) => dataLoad ? <Skeleton /> : rowData.ad_type} />
                        <Column
                            field="is_active"
                            header="Status"
                            sortable
                            style={{ minWidth: "110px" }}
                            body={(rowData) => dataLoad ? <Skeleton /> : (
                                <span
                                    className={`px-2 py-1 rounded text-white text-sm ${rowData.is_active ? "bg-green-500" : "bg-red-500"}`}
                                >
                                    {rowData.is_active ? "Active" : "Inactive"}
                                </span>
                            )}
                        />
                        <Column field="start_date" header="Start Date" sortable style={{ minWidth: "120px" }}
                            body={(rowData) =>
                                dataLoad ? <Skeleton /> : (rowData.start_date ? new Date(rowData.start_date).toLocaleDateString("en-GB") : "-")
                            }
                        />
                        <Column field="end_date" header="End Date" sortable style={{ minWidth: "120px" }}
                            body={(rowData) =>
                                dataLoad ? <Skeleton /> : (rowData.end_date ? new Date(rowData.end_date).toLocaleDateString("en-GB") : "-")
                            }
                        />
                        <Column field="city" header="District" sortable style={{ minWidth: "130px" }} body={(rowData) => dataLoad ? <Skeleton /> : rowData.city} />
                        <Column header="Action" frozen alignFrozen="right" style={{ minWidth: "380px" }} body={(rowData) => dataLoad ? <Skeleton /> : actionBodyTemplate(rowData)} />
                    </DataTable>
                )}
                {totalRecords > 0 && <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-2 bg-white p-2 mt-2 sm:mt-0" style={{ borderRadius: "8px" }}>
                    {/* PrimeReact Paginator */}
                    <Paginator
                        template={paginatoreTemplate}
                        first={startIndex}
                        rows={itemsPerPage}
                        totalRecords={totalRecords}
                    />

                    {/* Custom Pagination Buttons */}
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <Button
                            onClick={() => handlePageChange(currentPage - 1)}
                            className="pagination-button flex-shrink-0 px-2 sm:px-4"
                            disabled={currentPage === 0}
                        >
                            <span className="sm:hidden">Prev</span>
                            <span className="hidden sm:inline">Previous</span>
                        </Button>

                        <Button className="pagination-button flex-1 min-w-0 cursor-default px-1 sm:px-4">
                            <span className="pagination-text whitespace-nowrap text-xs sm:text-sm">
                                Page {currentPage + 1} of {totalPages || 1}
                            </span>
                        </Button>

                        <Button
                            onClick={() => handlePageChange(currentPage + 1)}
                            className="pagination-button flex-shrink-0 px-2 sm:px-4"
                            disabled={currentPage + 1 === (totalPages === 0 ? 1 : totalPages)}
                        >
                            Next
                        </Button>
                    </div>
                </div>}
            </div>
            {/* Message Dialog */}
            <Dialog
                header="Send Notification"
                visible={showMessageDialog}
                style={{ width: "90vw", maxWidth: "500px" }}
                modal
                draggable={false}
                onHide={() => setShowMessageDialog(false)}
            >

                <div className="flex flex-col gap-3">

                    {/* User Name */}
                    <div className="font-semibold text-sm">
                        To: {
                            selectedUser
                                ? selectedUser.fullName
                                : `${selectedUsers.length} Users`
                        }
                    </div>

                    <InputTextarea
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        rows={5}
                        placeholder="Enter your title..."
                        className="w-full"
                    />

                    {/* Message Box */}
                    <InputTextarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={5}
                        placeholder="Enter your message..."
                        className="w-full"
                    />

                    <WhatsAppTemplateSection
                        template={waTemplate}
                        params={waParams}
                        onTemplateChange={setWaTemplate}
                        onParamsChange={setWaParams}
                    />

                    {/* Buttons */}
                    <div className="flex justify-end gap-2 mt-3 flex-wrap">

                        <Button
                            label="Cancel"
                            severity="secondary"
                            onClick={() => setShowMessageDialog(false)}
                            disabled={sending || sendingWhatsApp}
                        />

                        <Button
                            label="Send WhatsApp"
                            icon={<WhatsAppIcon size={16} />}
                            severity="success"
                            loading={sendingWhatsApp}
                            disabled={!waTemplate || sending}
                            onClick={sendWhatsAppOnly}
                        />

                        <Button
                            label="Send"
                            icon="pi pi-send"
                            loading={sending}
                            disabled={sendingWhatsApp}
                            onClick={sendMessage}
                        />

                    </div>
                </div>
            </Dialog>

            <DeleteConfirmation
                visible={deleteConfirmationVisible}
                onHide={hideDeleteConfirmation}
                onDelete={confirmDelete}
                header="Delete Advertisement"
                message={`Are you sure you want to delete "${companyToDelete?.company_name}"? This action cannot be undone.`}
            />

            {/* Rerun Dialog */}
            <Dialog
                header="Rerun Advertisement"
                visible={rerunDialog}
                style={{ width: "90vw", maxWidth: "420px" }}
                modal
                draggable={false}
                onHide={() => { setRerunDialog(false); setRerunAd(null); }}
            >
                <div className="flex flex-col gap-4">
                    {rerunAd && (
                        <p className="text-sm text-gray-600 m-0 font-semibold">
                            {rerunAd.company_name}
                        </p>
                    )}
                    <p className="text-sm text-gray-500 m-0">
                        New dates thi advertisement reactivate thashe.
                    </p>

                    <div className="flex flex-col gap-1">
                        <label className="font-semibold text-sm">Start Date</label>
                        <Calendar
                            value={rerunStartDate}
                            onChange={(e) => setRerunStartDate(e.value as Date)}
                            showIcon
                            dateFormat="dd/mm/yy"
                            minDate={new Date()}
                            className="w-full"
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="font-semibold text-sm">End Date</label>
                        <Calendar
                            value={rerunEndDate}
                            onChange={(e) => setRerunEndDate(e.value as Date)}
                            showIcon
                            dateFormat="dd/mm/yy"
                            minDate={rerunStartDate || new Date()}
                            className="w-full"
                        />
                    </div>

                    <div className="flex justify-end gap-2 mt-2">
                        <Button
                            label="Cancel"
                            severity="secondary"
                            onClick={() => { setRerunDialog(false); setRerunAd(null); }}
                            disabled={rerunLoading}
                        />
                        <Button
                            label="Rerun"
                            icon="pi pi-refresh"
                            loading={rerunLoading}
                            onClick={handleRerunSubmit}
                        />
                    </div>
                </div>
            </Dialog>
            <ConfirmActionPopup
                visible={waConfirmVisible}
                onHide={() => { setWaConfirmVisible(false); setWaPendingPayload(null); }}
                onConfirm={confirmSendWhatsApp}
                header="Confirm WhatsApp Send"
                confirmLabel="Send"
                loading={sendingWhatsApp}
                message={
                    waPendingPayload
                        ? `This WhatsApp message will be sent to ${waPendingPayload.userIds.length} user${waPendingPayload.userIds.length === 1 ? "" : "s"}. Are you sure?`
                        : ""
                }
            />
        </>
    );
};

export default AdvertisementPage;
